import { ImportExportApiService } from 'src/app/data-transfer/services/import-export-api-service';
import { ImportTypeSetting, PackagingImportHelper } from './packaging-import-helper';
import { MaterialNavigationService } from 'src/app/navigation/services/navigation-services/material-navigation.service';
import { Observable } from 'rxjs';
import { ImportCandidateDto } from 'src/app/data-transfer/entities/import-candidate-dto';
import { ChangeReferenceDto } from 'src/app/data-transfer/entities/change-reference-dto';
import { PackagingPart } from 'src/app/model/packaging-part-enum';
import { PaginationDataSource } from 'src/app/util/pagination-datasource';

export class PackagingMaterialImportHelper implements PackagingImportHelper {
  private readonly importCandidatesDataSource = new PaginationDataSource<ImportCandidateDto>(query =>
    this.importExportApiService.getImportCandidatesByPage(PackagingPart.Material, query)
  );

  private readonly changeCandidatesDataSource = new PaginationDataSource<ChangeReferenceDto>(query =>
    this.importExportApiService.getChangesByPage(PackagingPart.Material, query)
  );

  constructor(
    private importExportApiService: ImportExportApiService,
    private materialNavigationService: MaterialNavigationService
  ) {}

  getImportCandidatesDataSource(): PaginationDataSource<ImportCandidateDto> {
    return this.importCandidatesDataSource;
  }

  getChangeCandidatesDataSource(): PaginationDataSource<ChangeReferenceDto> {
    return this.changeCandidatesDataSource;
  }

  getTableTitle(importType: ImportTypeSetting, archiveActive: boolean): string {
    if (importType === ImportTypeSetting.Import) {
      if(archiveActive) {
        return 'dataManagement.import.import.tableTitleMaterialArchive';
      }
      return 'dataManagement.import.import.tableTitleMaterial';
    }
    return 'dataManagement.import.changes.tableTitleMaterial';
  }

  getDisplayedColumns(): string[] {
    return ['itemId', 'version', 'exportingOrganization', 'articleName', 'articleNumber', 'timestamp', 'action', 'select'];
  }

  packagingImportCandidatesSeen(): Promise<any> {
    return this.importExportApiService.putMaterialImportNotificationsSeen();
  }

  packagingChangeCandidatesSeen(): Promise<any> {
    return this.importExportApiService.putMaterialTrackingtNotificationsSeen();
  }

  packagingImportCandidatesCountUpdate(): Promise<any> {
    return this.importExportApiService.updateMaterialImportCount();
  }

  packagingChangeCandidatesCountUpdate(): Promise<any> {
    return this.importExportApiService.updateMaterialTrackingCount();
  }

  getDeleteFunction(importType: ImportTypeSetting): (candidate: number) => Observable<any> {
    if (importType === ImportTypeSetting.Import) {
      return this.getDeleteFunctionImportCandidate();
    }
    return this.getDeleteFunctionChangeCandidate();
  }

  getImportFunctionImportCandidate(): (candidateId: number, targetDirId: number, isWithTracking: boolean) => Observable<any> {
    return (candidateId: number, targetDirId: number, isWithTracking: boolean) => this.importExportApiService.importMaterials(candidateId, targetDirId, isWithTracking);
  }

  getChangeFunctionChangeCandidate(): (changeId: number) => Observable<any> {
    return (changeId: number) => this.importExportApiService.applyMaterialChanges(changeId);
  }

  navigateToCandidate(candidate: ImportCandidateDto): void {
    this.materialNavigationService.navigateToMaterialPreview(candidate.id);
  }

  navigateToChange(candidate: ChangeReferenceDto, seePackagingBeforeChange: boolean): void {
    const id = seePackagingBeforeChange ? candidate.id : candidate.importCandidateId;
    this.materialNavigationService.navigateToMaterialPreview(id, true, seePackagingBeforeChange);
  }

  private getDeleteFunctionImportCandidate(): (candidate: number) => Observable<any> {
    return (candidateId: number) => this.importExportApiService.deleteMaterialImportCandidate(candidateId);
  }

  private getDeleteFunctionChangeCandidate(): (candidate: number) => Observable<any> {
    return (changeId: number) => this.importExportApiService.ignoreMaterialChanges(changeId);
  }
}
